const hasText = (value) => typeof value === "string" && value.trim().length > 0;

function amount(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const parsed = Number(typeof value === "string" ? value.trim().replace(",", ".") : value);
  return Number.isFinite(parsed) ? parsed : null;
}

function validDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

const validQuantity = (line) => amount(line?.quantity) !== null && amount(line.quantity) > 0;
const validPrice = (line) => amount(line?.unit_price) !== null && amount(line.unit_price) >= 0;
const sameMoney = (left, right) => Number.isFinite(left) && Number.isFinite(right)
  && Math.abs(Math.round(left * 100) - Math.round(right * 100)) <= 1;

function totalsResolved(lines, expenses, aiTotals) {
  if (!aiTotals || !lines.length || !lines.every((line) => validQuantity(line) && validPrice(line))
    || !expenses.every((expense) => amount(expense?.amount) !== null)) return false;

  const subtotal = amount(aiTotals.subtotal);
  const tax = amount(aiTotals.tax);
  const total = amount(aiTotals.total);
  // AI's subtotal includes the separate expenses, as validated by the API.
  // A gross OCR total alone cannot prove that a net draft now balances.
  if (subtotal !== null && tax !== null && total !== null && !sameMoney(subtotal + tax, total)) return false;
  const expected = subtotal ?? (total !== null && tax !== null ? total - tax : null);
  if (expected === null) return false;
  const sum = lines.reduce((value, line) => value + amount(line.quantity) * amount(line.unit_price), 0)
    + expenses.reduce((value, expense) => value + (expense.distribute_cost ? 0 : amount(expense.amount)), 0);
  return sameMoney(sum, expected);
}

// Only retire an extraction warning when the current draft proves it resolved.
// detectedTotal may be supplied by callers; without reliable tax/subtotal data
// it intentionally cannot dismiss total_mismatch.
export function filterResolvedOcrReviewFlags(flags, {
  supplierMatched = false,
  deliveryNumber = "",
  deliveryDate = "",
  dateConfirmed = false,
  lines = [],
  expenses = [],
  aiTotals = null,
} = {}) {
  const allLines = (predicate) => lines.length > 0 && lines.every(predicate);
  return (Array.isArray(flags) ? flags : []).filter((flag) => {
    switch (flag?.code) {
      case "missing_supplier":
      case "supplier_not_matched":
        return !supplierMatched;
      case "missing_document_number":
        return !hasText(deliveryNumber);
      case "missing_date":
        // The legacy UI defaults an unread date to today; that is not evidence.
        return !dateConfirmed || !validDate(deliveryDate);
      case "no_lines":
        return lines.length === 0;
      case "all_line_descriptions_missing":
      case "some_line_descriptions_missing":
        return !allLines((line) => hasText(line?.description));
      case "all_quantities_missing":
      case "some_quantities_missing":
        return !allLines(validQuantity);
      case "all_unit_prices_missing":
      case "some_unit_prices_missing":
        return !allLines(validPrice);
      case "all_line_totals_missing":
      case "some_line_totals_missing":
        return !allLines((line) => validQuantity(line) && validPrice(line));
      case "too_few_lines_for_multipage_document": {
        const pages = amount(flag.details?.document_pages);
        return !Number.isInteger(pages) || pages <= 1 || lines.length <= pages;
      }
      case "total_mismatch":
        return !totalsResolved(lines, expenses, aiTotals);
      default:
        return true;
    }
  });
}
